import { useEffect, useContext, useState } from 'react';
import axios from 'axios';
import { Box, IconButton, Snackbar, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { LanguageContext } from '../../../context/languageContext';
import {
  DataGridPremium,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import EditWorklogModal from './editWorklogModal';
import moment from 'moment/moment';

export default function RrfWorklog() {
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditWorklogModalOpen, setIsEditWorklogModalOpen] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const { translations } = useContext(LanguageContext);
  const apiRef = useGridApiRef();

  const rrfWorklogReport = translations => ({
    apiEndpoint: `${process.env.REACT_APP_ENDPOINT_REPORTING}/depot/benchrepair/rrfWorklogData`,
    columns: [
      {
        field: 'edit',
        headerName: 'Edit',
        renderCell: params => {
          if (
            params.rowNode &&
            params.rowNode.type !== 'group' &&
            params.row.currentStatus === 'Delivered'
          ) {
            return (
              <IconButton
                onClick={() => handleEditWorklogModalOpenClose(params.row)}
              >
                <EditIcon />
              </IconButton>
            );
          }
          return params.value;
        },
      },
      {
        field: 'repairOrder',
        headerName: 'Repair Order Number',
        width: 200,
      },
      {
        field: 'currentStatus',
        headerName: translations.shippingStatus || 'Shipping Status',
        width: 200,
      },
      {
        field: 'serialNumber',
        headerName: translations.serialNumber || 'Serial Number',
        width: 200,
      },
      {
        field: 'openCase',
        headerName: translations.openCase || 'Open Case',
        width: 200,
      },
      {
        field: 'caseNumber',
        headerName: translations.caseNumber || 'Case Number',
        width: 200,
      },
      {
        field: 'oem',
        headerName: 'OEM',
        width: 200,
      },
      {
        field: 'assetTag',
        headerName: translations.assetTag || 'Asset Tag',
        width: 200,
      },
      {
        field: 'failureMode',
        headerName: translations.failureMode || 'Failure Mode',
        width: 200,
      },
      {
        field: 'solution',
        headerName: translations.solution || 'Solution',
        width: 200,
      },
      {
        field: 'partsUsed',
        headerName: translations.partsUsed || 'Parts Used',
        width: 200,
      },
      {
        field: 'status',
        headerName: translations.status || 'Status',
        width: 200,
      },
      {
        field: 'totalWorkTime',
        headerName: translations.totalWorkTime || 'Total Work Time (minutes)',
        width: 200,
      },
      {
        field: 'isRepeatReturn',
        headerName: translations.repeatReturn || 'Repeat Return',
        width: 200,
      },
      {
        field: 'returnDate',
        headerName: translations.returnDate || 'Return Date',
        width: 200,
        renderCell: params => {
          if (params.rowNode && params.rowNode.type !== 'group') {
            if (params.value) {
              return moment(params.value).format('MM/DD/YYYY');
            } else {
              return 'N/A';
            }
          }
          return '';
        },
      },
      {
        field: 'datetime',
        headerName: 'Date Submitted',
        width: 200,
        renderCell: params => {
          if (params.rowNode && params.rowNode.type !== 'group') {
            if (params.value) {
              return moment(params.value).format('MM/DD/YYYY hh:mm A');
            } else {
              return 'N/A';
            }
          }
          return '';
        },
      },
      {
        field: 'startDate',
        headerName: translations.startDate || 'Start Date',
        width: 200,
        renderCell: params => {
          if (params.rowNode && params.rowNode.type !== 'group') {
            if (params.value) {
              return moment(params.value).format('MM/DD/YYYY');
            } else {
              return 'N/A';
            }
          }
          return '';
        },
      },
      {
        field: 'completionDate',
        headerName: translations.completionDate || 'Completion Date',
        width: 200,
        renderCell: params => {
          if (params.rowNode && params.rowNode.type !== 'group') {
            if (params.value) {
              return moment(params.value).format('MM/DD/YYYY');
            } else {
              return 'N/A';
            }
          }
          return '';
        },
      },
      {
        field: 'comments',
        headerName: translations.comments || 'Comments',
        width: 400,
      },
      {
        field: 'jurisdiction',
        headerName: 'Jurisdiction',
        width: 200,
      },
      {
        field: 'product',
        headerName: translations.product || 'Product',
        width: 200,
      },
      {
        field: 'rmaNumber',
        headerName: 'RMA Number',
        width: 200,
      },
    ],
  });

  const rrfWorklog = rrfWorklogReport(translations);

  const handleEditWorklogModalOpenClose = entry => {
    setCurrentEntry(isEditWorklogModalOpen ? null : entry);
    setIsEditWorklogModalOpen(!isEditWorklogModalOpen);
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.get(rrfWorklog.apiEndpoint);
      setData({
        rows: response.data,
      });
      setIsLoading(false);
    };
    fetchData();
  }, [rrfWorklog.apiEndpoint]);

  const handleRefresh = async () => {
    const response = await axios.get(rrfWorklog.apiEndpoint);
    setData({
      rows: response.data,
    });
  };

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: ['repairOrder'],
      },
      filter: {
        filterModel: {
          items: [
            { field: 'currentStatus', operator: 'equals', value: 'Delivered' },
          ],
        },
      },
    },
  });

  return (
    <Box
      sx={{
        height: '80vh',
        width: '100%',
        backgroundColor: '#ffffff',
        color: '#000000',
      }}
    >
      <DataGridPremium
        {...data}
        pagination
        autoPageSize
        loading={isLoading}
        columns={rrfWorklog.columns}
        label='RRF Worklog'
        disableRowSelectionOnClick
        showToolbar
        sx={{ fontFamily: 'BrightstarSans-Regular' }}
        initialState={initialState}
        apiRef={apiRef}
      />
      {isEditWorklogModalOpen && (
        <EditWorklogModal
          handleRefresh={handleRefresh}
          isOpen={isEditWorklogModalOpen}
          handleClose={handleEditWorklogModalOpenClose}
          entry={currentEntry}
          setIsSnackbarOpen={setIsSnackbarOpen}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
        />
      )}
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
